import { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

type EquipmentType = 'panel' | 'inverter';

interface PanelGroup {
  id: string;
  manufacturer: string;
  model: string;
  technology: string | null;
  nominalPowerW: number | string | null;
  panelCount: number | null;
  notes: string | null;
}
interface InverterGroup {
  id: string;
  manufacturer: string;
  model: string;
  inverterCount: number;
  nominalPowerKw: number | string;
  modbusProtocol: string | null;
  modbusHost: string | null;
  modbusPort: number | null;
  modbusUnitId: number | null;
  unitIds: string | null;
  notes: string | null;
}
type Group = PanelGroup | InverterGroup;

interface Props {
  type: EquipmentType;
  assetId: string;
}

function useEquipmentColors(isDark: boolean) {
  return {
    cardBg: 'var(--surface-solid)',
    cardBorder: 'var(--line)',
    textMain: 'var(--text)',
    textMuted: 'var(--text-muted)',
    mutedText: 'var(--text-muted)',
    tableHeaderBg: 'var(--surface-2)',
    tableBorder: 'var(--surface-2)',
    iconBg: 'var(--surface-2)',
    inputBg: 'var(--surface-solid)',
    inputBorder: 'var(--line-strong)',
    modalOverlay: 'rgba(0,0,0,0.5)',
    footerBg: 'var(--surface-2)',
    footerBorder: 'var(--line)',
    sectionBg: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-2)',
    sectionBorder: 'var(--line)',
    cancelBg: 'var(--surface-solid)',
    badgeBg: 'var(--surface-2)',
  };
}

// Підписи кнопки додавання й заголовок модалки — цілі фрази на кожен тип
// обладнання. Українською «Додати групу модулів» не збирається зі слів
// «Додати» + «модуль» + «група» без відмінювання, тому ключ один на фразу.
const GROUP_KEYS = {
  panel:    { add: 'eq_add_panel',    edit: 'eq_edit_panel' },
  inverter: { add: 'eq_add_inverter', edit: 'eq_edit_inverter' },
} as const;

export default function EquipmentGroupsSection({ type, assetId }: Props) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const clr = useEquipmentColors(isDark);
  const meta = {
    panel: {
      label: t('eq_panel_groups'),
      subtitle: t('eq_panel_sub'),
      endpoint: 'panels',
      icon: 'ri-layout-grid-line',
      iconBg: 'var(--surface-2)',
      iconColor: 'var(--text-muted)',
    },
    inverter: {
      label: t('eq_inverter_groups'),
      subtitle: t('eq_inverter_sub'),
      endpoint: 'inverters',
      icon: 'ri-pulse-line',
      iconBg: 'var(--surface-2)',
      iconColor: 'var(--text-muted)',
    },
  }[type];

  const [rows, setRows] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { addToast } = useToast();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<Group[]>(`/assets/${assetId}/${meta.endpoint}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      addToast(getApiErrorMessage(e, t('eq_load_failed')), 'error');
    } finally {
      setLoading(false);
    }
  }, [assetId, meta.endpoint, addToast]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleAdd = () => { setEditingGroup(null); setModalOpen(true); };
  const handleEdit = (g: Group) => { setEditingGroup(g); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); fetchRows(); };
  const handleDelete = async (g: Group) => {
    const label = type === 'panel'
      ? `${g.manufacturer} ${g.model}`
      : `${g.manufacturer} ${(g as InverterGroup).modbusPort ? ':' + (g as InverterGroup).modbusPort : ''}`.trim();
    if (!window.confirm(t('eq_delete_confirm', { name: label }))) return;
    try {
      await api.delete(`/assets/${assetId}/${meta.endpoint}/${g.id}`);
      addToast(t('common_deleted'), 'success');
      fetchRows();
    } catch (e) {
      addToast(getApiErrorMessage(e, t('common_delete_failed')), 'error');
    }
  };

  return (
    <div className="rounded-xl p-5 mb-6" style={{ background: clr.cardBg, border: `1px solid ${clr.cardBorder}` }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: meta.iconBg }}>
            <i className={`${meta.icon} text-sm`} style={{ color: meta.iconColor }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: clr.textMain }}>{meta.label}</h3>
            <p className="text-xs" style={{ color: clr.textMuted }}>{meta.subtitle}</p>
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="text-xs font-medium px-3 py-1.5 rounded-md cursor-pointer transition-colors whitespace-nowrap"
          style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
        >
          <i className="ri-add-line text-xs mr-1"></i>
          {t(GROUP_KEYS[type].add)}
        </button>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-center py-6 text-xs" style={{ color: clr.textMuted }}>
          <i className="ri-loader-4-line animate-spin mr-1"></i> Loading...
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-center py-8 text-xs" style={{ color: clr.mutedText }}>
          No {type === 'panel' ? 'panel' : 'inverter'} groups configured.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: clr.tableHeaderBg, color: clr.textMuted }}>
                <th className="text-left px-3 py-2 font-medium">{t('eq_manufacturer')}</th>
                <th className="text-left px-3 py-2 font-medium">{t('eq_model')}</th>
                {type === 'panel' && <th className="text-right px-3 py-2 font-medium">{t('eq_power_w')}</th>}
                {type === 'panel' && <th className="text-right px-3 py-2 font-medium">{t('eq_count')}</th>}
                {type === 'inverter' && <th className="text-right px-3 py-2 font-medium">{t('eq_power_kw')}</th>}
                {type === 'inverter' && <th className="text-right px-3 py-2 font-medium">{t('eq_count')}</th>}
                {type === 'inverter' && <th className="text-left px-3 py-2 font-medium">Modbus</th>}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => (
                <tr key={g.id} style={{ borderTop: `1px solid ${clr.tableBorder}` }}>
                  <td className="px-3 py-2" style={{ color: clr.textMain }}>{g.manufacturer}</td>
                  <td className="px-3 py-2" style={{ color: clr.textMain }}>{g.model}</td>
                  {type === 'panel' && (
                    <>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {(g as PanelGroup).nominalPowerW != null ? Number((g as PanelGroup).nominalPowerW).toFixed(0) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {(g as PanelGroup).panelCount ?? '—'}
                      </td>
                    </>
                  )}
                  {type === 'inverter' && (
                    <>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {Number((g as InverterGroup).nominalPowerKw).toFixed(0)}
                      </td>
                      <td className="px-3 py-2 text-right" style={{ color: clr.textMain }}>
                        {(g as InverterGroup).inverterCount}
                      </td>
                      <td className="px-3 py-2" style={{ color: clr.textMuted, fontFamily: 'monospace' }}>
                        {(g as InverterGroup).modbusHost
                          ? `${(g as InverterGroup).modbusHost}:${(g as InverterGroup).modbusPort ?? '—'}`
                          : <span style={{ color: clr.mutedText }}>{t('eq_not_configured')}</span>}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => handleEdit(g)} className="text-xs px-2 py-1 rounded cursor-pointer inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
                      <Pencil size={13} strokeWidth={2} />{t('common_edit')}
                    </button>
                    <button onClick={() => handleDelete(g)} title={t('common_delete')} aria-label={t('common_delete')} className="text-xs px-2 py-1 rounded cursor-pointer ml-1" style={{ color: 'var(--text-muted)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--err)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                      >
                      <Trash2 size={13} strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <EquipmentModal
          type={type}
          assetId={assetId}
          endpoint={meta.endpoint}
          group={editingGroup}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

// ============================================================================
// Modal component (kept in same file for compactness)
// ============================================================================

interface ModalProps {
  type: EquipmentType;
  assetId: string;
  endpoint: string;
  group: Group | null;
  onClose: () => void;
  onSaved: () => void;
}

function EquipmentModal({ type, assetId, endpoint, group, onClose, onSaved }: ModalProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const clr = useEquipmentColors(isDark);
  const isEdit = !!group;
  const [form, setForm] = useState<Record<string, any>>(() => {
    if (group) return { ...group };
    return type === 'panel'
      ? { manufacturer: '', model: '', technology: '', nominalPowerW: '', panelCount: '', notes: '' }
      : { manufacturer: '', model: '', inverterCount: '', nominalPowerKw: '', modbusProtocol: '', modbusHost: '', modbusPort: '', modbusUnitId: '', unitIds: '', notes: '' };
  });
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();

  const set = (k: string, v: any) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = useCallback(async (saveAndAddAnother: boolean = false) => {
    setSaving(true);
    try {
      const payload: Record<string, any> = {};
      for (const [k, v] of Object.entries(form)) {
        if (['nominalPowerW', 'panelCount', 'inverterCount', 'nominalPowerKw', 'modbusPort', 'modbusUnitId'].includes(k)) {
          payload[k] = v === '' || v == null ? null : Number(v);
        } else {
          payload[k] = v === '' ? null : v;
        }
      }
      if (isEdit) {
        await api.patch(`/assets/${assetId}/${endpoint}/${group!.id}`, payload);
        addToast(t('common_saved'), 'success');
        onSaved();
      } else {
        await api.post(`/assets/${assetId}/${endpoint}`, payload);
        addToast(t('common_added'), 'success');
        if (saveAndAddAnother) {
          setForm((prev) => {
            const fresh: Record<string, any> = type === 'panel'
              ? { manufacturer: prev.manufacturer || '', model: '', technology: '', nominalPowerW: '', panelCount: '', notes: '' }
              : { manufacturer: prev.manufacturer || '', model: '', inverterCount: '', nominalPowerKw: '', modbusProtocol: prev.modbusProtocol || '', modbusHost: prev.modbusHost || '', modbusPort: '', modbusUnitId: '', unitIds: '', notes: '' };
            return fresh;
          });
        } else {
          onSaved();
        }
      }
    } catch (e) {
      addToast(getApiErrorMessage(e, t('common_save_failed')), 'error');
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, assetId, endpoint, group, onSaved, addToast, type]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: clr.modalOverlay }} onClick={onClose}>
      <div className="rounded-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ background: clr.cardBg }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${clr.cardBorder}` }}>
          <h3 className="text-base font-semibold" style={{ color: clr.textMain }}>
            {t(GROUP_KEYS[type][isEdit ? 'edit' : 'add'])}
          </h3>
          <button onClick={onClose} className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
            <i className="ri-close-line" style={{ color: clr.textMuted }}></i>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Equipment info */}
          <div>
            <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: clr.mutedText }}>{t('eq_equipment')}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={`${t('eq_manufacturer')} *`} value={form.manufacturer} onChange={(v) => set('manufacturer', v)} placeholder="SMA, Longi Solar..." isDark={isDark} clr={clr} />
              <Field label={`${t('eq_model')} *`} value={form.model} onChange={(v) => set('model', v)} placeholder="STP 50-40, LR-72PE..." isDark={isDark} clr={clr} />
              {type === 'panel' && (
                <>
                  <Field label={t('eq_technology')} value={form.technology} onChange={(v) => set('technology', v)} placeholder="mono / poly / thin-film" isDark={isDark} clr={clr} />
                  <Field label={`${t('eq_nominal_power_w')} *`} type="number" value={form.nominalPowerW} onChange={(v) => set('nominalPowerW', v)} placeholder="365" isDark={isDark} clr={clr} />
                  <Field label={t('eq_panel_count')} type="number" value={form.panelCount} onChange={(v) => set('panelCount', v)} placeholder={t('eq_ph_optional')} isDark={isDark} clr={clr} />
                </>
              )}
              {type === 'inverter' && (
                <>
                  <Field label={`${t('eq_nominal_power_kw')} *`} type="number" value={form.nominalPowerKw} onChange={(v) => set('nominalPowerKw', v)} placeholder="50" isDark={isDark} clr={clr} />
                  <Field label={`${t('eq_inverter_count')} *`} type="number" value={form.inverterCount} onChange={(v) => set('inverterCount', v)} placeholder="28" isDark={isDark} clr={clr} />
                </>
              )}
            </div>
          </div>

          {/* Modbus config — inverters only */}
          {type === 'inverter' && (
            <div>
              <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: clr.mutedText }}>{t('eq_modbus_section')}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('eq_protocol')} value={form.modbusProtocol} onChange={(v) => set('modbusProtocol', v)} placeholder="sma-sunspec / huawei-smartlogger / fronius / growatt" isDark={isDark} clr={clr} />
                <Field label={t('eq_host')} value={form.modbusHost} onChange={(v) => set('modbusHost', v)} placeholder="93.170.53.225" isDark={isDark} clr={clr} />
                <Field label={t('eq_port')} type="number" value={form.modbusPort} onChange={(v) => set('modbusPort', v)} placeholder="502 / 5020 / 5023" isDark={isDark} clr={clr} />
                <Field label={t('eq_unit_id_plant')} type="number" value={form.modbusUnitId} onChange={(v) => set('modbusUnitId', v)} placeholder={t('eq_ph_unit_id')} isDark={isDark} clr={clr} />
                <Field label={t('eq_unit_ids_csv')} value={form.unitIds} onChange={(v) => set('unitIds', v)} placeholder="11,12,13,14,15..." wide isDark={isDark} clr={clr} />
              </div>
            </div>
          )}

          <div>
            <div className="text-[11px] font-semibold uppercase mb-2" style={{ color: clr.mutedText }}>{t('eq_notes')}</div>
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)}
              rows={2}
              className="w-full text-xs px-3 py-2 rounded-md outline-none"
              style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
              placeholder={t('eq_ph_notes')}
            />
          </div>
        </div>

        <div className="px-6 py-4 flex items-center justify-end gap-2" style={{ borderTop: `1px solid ${clr.footerBorder}`, background: clr.footerBg }}>
          <button onClick={onClose} className="text-xs px-4 py-2 rounded-md cursor-pointer" style={{ background: clr.cancelBg, border: `1px solid ${clr.cardBorder}`, color: clr.textMain }}>
            {t('common_cancel')}
          </button>
          {!isEdit && (
            <button onClick={() => save(true)} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer" style={{ background: clr.cancelBg, border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
              {t('eq_save_add_another')}
            </button>
          )}
          <button onClick={() => save(false)} disabled={saving} className="text-xs px-4 py-2 rounded-md cursor-pointer font-medium" style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}>
            {saving ? t('common_saving') : (isEdit ? t('common_save') : t('common_add'))}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, placeholder, wide, isDark, clr }: { label: string; value: any; onChange: (v: string) => void; type?: string; placeholder?: string; wide?: boolean; isDark: boolean; clr: ReturnType<typeof useEquipmentColors> }) {
  return (
    <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="text-[11px] mb-1 block" style={{ color: clr.textMuted }}>{label}</span>
      <input
        type={type || 'text'}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs px-3 py-2 rounded-md outline-none"
        style={{ border: `1px solid ${clr.inputBorder}`, color: clr.textMain, background: clr.inputBg }}
      />
    </label>
  );
}